import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { authStore } from '../api/client';
import {
  listQuestions, createQuestion, updateQuestion, deleteQuestion,
} from '../api/questions';
import PageHeader from '../components/ui/PageHeader';
import EmptyState from '../components/ui/EmptyState';
import Modal from '../components/ui/Modal';
import { useToast } from '../components/ui/Toast';
import QuestionCard from '../components/questions/QuestionCard';
import QuestionFilters from '../components/questions/QuestionFilters';
import QuestionForm from '../components/questions/QuestionForm';
import QuestionDetailDrawer from '../components/questions/QuestionDetailDrawer';
import BlueprintLibrary from '../components/blueprints/BlueprintLibrary';

const TABS = [
  { id: 'library', label: 'Samastix Library' },
  { id: 'org', label: 'My Organization' },
  { id: 'questions', label: 'Fixed questions' },
];

const PAGE_SIZE = 20;

/**
 * QuestionsPage - Phase 1 question bank manager.
 * Route: /questions
 */
export default function QuestionsPage() {
  const role  = authStore.role;
  const toast = useToast();
  const canMutate = role === 'SUPER_ADMIN' || role === 'ORG_ADMIN';

  const [filters,  setFilters]  = useState({ search: '', category: '', difficulty: '', active: undefined });
  const [page,     setPage]     = useState(0);
  const [data,     setData]     = useState({ content: [], totalElements: 0, totalPages: 0 });
  const [loading,  setLoading]  = useState(false);

  const [createOpen, setCreateOpen] = useState(false);
  const [editing,    setEditing]    = useState(null);  // question being edited
  const [detail,     setDetail]     = useState(null);  // question shown in drawer
  const [submitting, setSubmitting] = useState(false);
  const [tab,        setTab]        = useState('library');

  const params = useMemo(() => {
    const p = { page, size: PAGE_SIZE };
    if (filters.search)     p.search     = filters.search;
    if (filters.category)   p.category   = filters.category;
    if (filters.difficulty) p.difficulty = filters.difficulty;
    if (filters.active === false) p.active = false;
    return p;
  }, [filters, page]);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listQuestions(params);
      setData(res.data);
    } catch (err) {
      toast.error('Failed to load questions');
      console.error('[QuestionsPage] list failed', err);
    } finally {
      setLoading(false);
    }
  }, [params, toast]);

  // Debounce search-driven reloads slightly
  useEffect(() => {
    const t = setTimeout(reload, 200);
    return () => clearTimeout(t);
  }, [reload]);

  const onCreate = async (payload) => {
    setSubmitting(true);
    try {
      await createQuestion(payload);
      toast.success('Question created');
      setCreateOpen(false);
      setPage(0);
      reload();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Create failed');
    } finally {
      setSubmitting(false);
    }
  };

  const onUpdate = async (payload) => {
    setSubmitting(true);
    try {
      const res = await updateQuestion(editing.id, payload);
      toast.success('Question updated');
      setEditing(null);
      setDetail(res.data);
      reload();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Update failed');
    } finally {
      setSubmitting(false);
    }
  };

  const onArchive = async (q) => {
    if (!q?.active) return;
    if (!window.confirm('Archive this question? It will be hidden from new pickers.')) return;
    try {
      await deleteQuestion(q.id);
      toast.success('Question archived');
      setDetail(null);
      reload();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Archive failed');
    }
  };

  return (
    <div className="page-pad page-y space-y-5 max-w-[1400px] mx-auto">
      <PageHeader
        title="Interview Blueprints"
        subtitle="What each role is assessed on. Fixed questions remain available for manual and must-ask use."
        sticky={false}
        action={tab === 'questions' && canMutate ? (
          <button
            onClick={() => setCreateOpen(true)}
            className="btn btn-sm btn-primary"
          >
            + New question
          </button>
        ) : null}
      />

      <div className="flex gap-1 border-b border-slate-200" role="tablist">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={tab === t.id}
            onClick={() => setTab(t.id)}
            className={`px-4 py-2 text-sm -mb-px border-b-2 ${tab === t.id
              ? 'border-teal-600 text-teal-700 font-medium'
              : 'border-transparent text-slate-500 hover:text-slate-700'}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab !== 'questions' ? <BlueprintLibrary key={tab} scope={tab} /> : (<>
      <QuestionFilters
        value={filters}
        onChange={(next) => { setFilters(next); setPage(0); }}
      />

      {loading ? (
        <div className="card py-16 text-sm text-slate-500 text-center rounded-xl">Loading…</div>
      ) : data.content.length === 0 ? (
        <EmptyState
          title="No questions yet"
          subtitle={canMutate
            ? 'Create your first question to start building a reusable bank.'
            : role === 'INTERVIEWER'
              ? 'Browse and attach questions to sessions. Creating or editing requires org or platform admin.'
              : 'No questions match the current filters.'}
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {data.content.map((q) => (
            <QuestionCard
              key={q.id}
              question={q}
              onClick={() => setDetail(q)}
            />
          ))}
        </div>
      )}

      {data.totalPages > 1 && (
        <div className="flex items-center justify-between pt-2 text-xs text-slate-600">
          <span>Page {page + 1} of {data.totalPages} · {data.totalElements} total</span>
          <div className="flex gap-2">
            <button
              type="button"
              disabled={page === 0}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              className="btn btn-sm btn-secondary px-3 disabled:opacity-40"
            >Previous</button>
            <button
              type="button"
              disabled={page + 1 >= data.totalPages}
              onClick={() => setPage((p) => p + 1)}
              className="btn btn-sm btn-secondary px-3 disabled:opacity-40"
            >Next</button>
          </div>
        </div>
      )}

      </>)}

      {/* Create modal */}
      <Modal
        open={createOpen}
        onClose={() => !submitting && setCreateOpen(false)}
        title="New question"
        size="lg"
      >
        <QuestionForm
          onSubmit={onCreate}
          onCancel={() => !submitting && setCreateOpen(false)}
          submitting={submitting}
        />
      </Modal>

      {/* Edit modal */}
      <Modal
        open={!!editing}
        onClose={() => !submitting && setEditing(null)}
        title="Edit question"
        size="lg"
      >
        {editing && (
          <QuestionForm
            initial={editing}
            onSubmit={onUpdate}
            onCancel={() => !submitting && setEditing(null)}
            submitting={submitting}
          />
        )}
      </Modal>

      {/* Detail drawer */}
      <QuestionDetailDrawer
        open={!!detail && !editing}
        onClose={() => setDetail(null)}
        question={detail}
        canEdit={canMutate}
        onEdit={() => setEditing(detail)}
        onArchive={() => onArchive(detail)}
      />
    </div>
  );
}
